import type { PageId } from '../../routes.ts';

/**
 * `footer` — `template.html` dòng 1054–1066.
 *
 * File RIÊNG chứ không nhồi thêm vào `common.ts`: footer thật của lô D dài (3 cột link + tagline +
 * dòng tên miền), để trong file dùng chung là chôn nội dung của một lô vào chỗ mọi lô đọc. Khoá
 * `rights` chuyển từ `common.footer` sang đây trong CÙNG commit để không còn hai nguồn sự thật.
 *
 * Cột "Liên hệ" là ba `<span>`, KHÔNG phải link — đúng thiết kế `:1062`. Đừng "sửa" thành `<a>`:
 * "Đặt lịch demo" ở đó là nhãn thông tin, chưa có đích thật (cùng lý do với hai nút `#cta`).
 *
 * `href` của 7 link đều là anchor trong trang trừ `newera.inc`. Ba trong số đó (`#security`
 * `#pricing` `#faq`) do CHÍNH lô D sinh ra — trước lô D, footer dựng đúng thiết kế sẽ có 3 link
 * chết. Đó là lý do footer không được land trước khi 4 section vào `HomePage`.
 */
/**
 * UNION có chủ ý, hai nhánh KHÔNG thay nhau được:
 *   · `href` — anchor trong trang (`#security`) hoặc link RA NGOÀI (`https://newera.inc/vi`).
 *   · `page` — trang NỘI BỘ, khai bằng id route. Đường dẫn do `pathFor()` sinh lúc render.
 *
 * Vì sao trang nội bộ KHÔNG được dùng `href`: gõ `/vi/privacy/` bằng tay là (1) dựng nguồn sự thật
 * thứ hai về slug bên cạnh bảng route, và (2) mở cửa cho link CHÉO ngôn ngữ — footer render trên
 * MỌI trang, nên một href cứng `/vi/…` sẽ kéo người đọc bản `en` sang bản `vi` và làm Google gộp
 * hai bản. `PageLink` lấy locale từ context nên không thể quên prefix. Đừng lách bằng `href`.
 */
type FooterLink =
  | { readonly href: string; readonly label: string }
  | { readonly page: PageId; readonly label: string };

interface FooterColumn {
  readonly label: string;
  readonly links: readonly FooterLink[];
}

export const footer = {
  tagline: 'Nexa là sản phẩm của NewEra Inc. — niềm tin và công nghệ, được tái định nghĩa.',
  /** `alt` của logo wordmark; nó mang tên tổ chức nên KHÔNG để rỗng. */
  logoAlt: 'NewEra Inc.',
  columns: [
    {
      label: 'Sản phẩm',
      links: [
        { href: '#modules', label: 'Tính năng' },
        { href: '#collab', label: 'Chat & Topic' },
        { href: '#pm', label: 'Quản lý dự án' },
        { href: '#ai', label: 'AI Agents' },
      ],
    },
    {
      label: 'Công ty',
      links: [
        { href: 'https://newera.inc/vi', label: 'newera.inc' },
        { href: '#security', label: 'Bảo mật' },
        { href: '#pricing', label: 'Bảng giá' },
        { href: '#faq', label: 'FAQ' },
      ],
    },
    /*
     * Cột PHÁP LÝ — thêm ở lô này, và nó KHÔNG có trong bản thiết kế (`template.html:1054` chỉ có
     * 3 cột: Sản phẩm · Công ty · Liên hệ). Đây là ngoại lệ user chốt 2026-08-27, không phải chép
     * sai thiết kế: 5 trang này đã tồn tại và đã prerender từ lâu, nhưng footer không trỏ tới trang
     * nào cả — tức chúng sống mà không ai tới được, và không cổng nào đỏ vì mỗi trang tự nó vẫn
     * đúng. Một site công khai phải có đường đi tới chính sách, điều khoản, cookie, kênh hỗ trợ và
     * quyền xoá tài khoản; thiếu là rớt app review và trái GDPR.
     *
     * Tập trang lấy theo nhánh `v1` (5 link, đúng thứ tự này) — `v1:src/lib/components/Footer.svelte`.
     * KHÔNG có `terms/v1` và `privacy/v1`: chúng là văn bản đã hết hiệu lực, gắn `noindex`, và đường
     * tới chúng là `VersionSwitcher` NẰM TRONG trang hiện hành. Đưa lên footer là mời người đọc vào
     * bản cũ như thể nó đang áp dụng. Cổng canh cả hai chiều: `copy.test.ts` ràng tập này khớp đúng
     * `LEGAL_PAGES` lọc theo `indexable`, nên thêm trang pháp lý mới mà quên footer là ĐỎ.
     *
     * LƯU Ý — NHÃN Ở ĐÂY LÀ NHÃN ĐIỀU HƯỚNG, KHÔNG PHẢI TIÊU ĐỀ VĂN BẢN — đừng "dọn" về
     * `legalDocMeta(id, locale).title`. Đã đo: tiêu đề thật là `Yêu cầu xóa tài khoản Nexa` (26 ký
     * tự) và `Request Nexa account deletion` (29). Cột footer rộng ~180px ở 1440 nên hai chuỗi đó
     * vỡ 3 dòng và phá lưới. Tiêu đề văn bản vẫn là h1 của trang và vẫn TRA từ docMeta ở
     * `copy/<locale>/legal.ts` — chỗ đó không đổi. Hai chuỗi khác nhau vì hai VAI khác nhau.
     */
    {
      label: 'Pháp lý',
      links: [
        { page: 'legal-privacy', label: 'Quyền riêng tư' },
        { page: 'legal-terms', label: 'Điều khoản' },
        { page: 'legal-cookies', label: 'Cookie' },
        { page: 'legal-support', label: 'Hỗ trợ' },
        { page: 'legal-delete-account', label: 'Xóa tài khoản' },
      ],
    },
    /*
     * Cột ỨNG DỤNG — cũng KHÔNG có trong thiết kế; user chốt 2026-09-28 cùng lúc với section
     * `#download`. Cả hai link trỏ về `#download`, KHÔNG thẳng vào file cài: section đó mới là chỗ
     * nói yêu cầu hệ thống (chip Apple, Windows 64-bit), và bấm tải từ footer mà không đọc dòng đó
     * là cách chắc nhất để người dùng Mac Intel tải về một bản không chạy.
     */
    {
      label: 'Ứng dụng',
      links: [
        { href: '#download', label: 'macOS' },
        { href: '#download', label: 'Windows' },
      ],
    },
  ] as readonly FooterColumn[],
  contact: {
    label: 'Liên hệ',
    items: ['Đặt lịch demo', 'hello@newera.inc', 'TP. Hồ Chí Minh, Việt Nam'] as readonly string[],
  },
  rights: 'Bảo lưu mọi quyền.',
};
